/**
 * Store Detail Component
 *
 * Full-width detail panel with tabs for Live Ops, Inventory, and Configuration.
 * Slides in when a store card is clicked.
 *
 * @module StoreCommandCenter/components
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Speed as LiveOpsIcon,
  Inventory as InventoryIcon,
  Settings as ConfigIcon,
  Lock as LockIcon,
  FiberManualRecord as StatusDot,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import type { StoreCommandData } from '../hooks/useCommandCenter';
import LiveOpsTab from './tabs/LiveOpsTab';
import InventoryTab from './tabs/InventoryTab';
import ConfigTab from './tabs/ConfigTab';

// ============================================================================
// Types
// ============================================================================

interface StoreDetailProps {
  store: StoreCommandData;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// Constants
// ============================================================================

const colors = {
  bgDeepest: 'var(--canvas)',
  bgCard: 'var(--panel)',
  bgElevated: 'var(--raised)',
  textPrimary: 'var(--tx)',
  textSecondary: 'var(--tx2)',
  textMuted: 'var(--tx3)',
  border: 'var(--line-soft)',
  successGlow: '#00FF88',
  errorGlow: '#FF003C',
  warning: '#FFB800',
  info: '#00B4FF',
};

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Tab panel wrapper
 */
function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`store-tabpanel-${index}`}
      aria-labelledby={`store-tab-${index}`}
      sx={{ py: 3 }}
    >
      {value === index && children}
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Store Detail with tabbed view
 */
export function StoreDetail({ store, onClose }: StoreDetailProps) {
  const [activeTab, setActiveTab] = useState(0);

  // Check if user is admin (has config access)
  const storedRoles = localStorage.getItem('userRoles');
  const roles = storedRoles ? JSON.parse(storedRoles) : [];
  const isAdmin = roles.includes('admin');

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Header status: an inactive store is unreachable by customers whatever its
  // hours say, so it must never read OPEN (Diep River did for months after it
  // was deactivated). Mirrors the card: inactive/paused outrank open/closed.
  const status = store.isActive === false
    ? { label: 'INACTIVE', color: colors.warning }
    : store.isPaused
      ? { label: 'PAUSED', color: colors.warning }
      : store.isOpen
        ? { label: 'OPEN', color: colors.successGlow }
        : { label: 'CLOSED', color: colors.errorGlow };

  return (
    <Paper
      sx={{
        bgcolor: colors.bgCard,
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${colors.border}`,
          background: 'linear-gradient(180deg, rgba(42, 42, 42, 0.8) 0%, rgba(30, 30, 30, 0.8) 100%)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton
              onClick={onClose}
              sx={{
                color: colors.textSecondary,
                bgcolor: colors.bgElevated,
                '&:hover': {
                  bgcolor: colors.border,
                  color: colors.textPrimary,
                },
              }}
            >
              <BackIcon />
            </IconButton>
          </Tooltip>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: colors.textPrimary, letterSpacing: '-0.02em' }}
              >
                {store.name}
              </Typography>
              <Chip
                icon={
                  <StatusDot
                    sx={{
                      fontSize: 10,
                      color: status.color,
                    }}
                  />
                }
                label={status.label}
                size="small"
                sx={{
                  bgcolor: `${status.color}15`,
                  color: status.color,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  border: `1px solid ${status.color}30`,
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
              {store.address && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    {[store.address.suburb, store.address.city].filter(Boolean).join(', ')}
                  </Typography>
                </Box>
              )}
              {store.phoneNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    {store.phoneNumber}
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                ID: {store.externalId}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Quick stats in header */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
              Products
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
              {store.inventory.productCount.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
              Stock
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
              {store.inventory.totalStock.toLocaleString()}
            </Typography>
          </Box>
          {store.inventory.lowStockCount > 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: colors.warning, display: 'block' }}>
                Low Stock
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ color: colors.warning }}>
                {store.inventory.lowStockCount}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: `1px solid ${colors.border}` }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 2,
            '& .MuiTab-root': {
              color: colors.textSecondary,
              fontWeight: 500,
              fontSize: '0.875rem',
              textTransform: 'none',
              minHeight: 48,
              '&.Mui-selected': {
                color: colors.textPrimary,
              },
            },
            '& .MuiTabs-indicator': {
              bgcolor: colors.info,
              height: 3,
            },
          }}
        >
          <Tab
            icon={<LiveOpsIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Live Ops"
            id="store-tab-0"
            aria-controls="store-tabpanel-0"
          />
          <Tab
            icon={<InventoryIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Inventory"
            id="store-tab-1"
            aria-controls="store-tabpanel-1"
          />
          <Tab
            icon={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ConfigIcon sx={{ fontSize: 20 }} />
                {!isAdmin && <LockIcon sx={{ fontSize: 14, color: colors.warning }} />}
              </Box>
            }
            iconPosition="start"
            label="Configuration"
            id="store-tab-2"
            aria-controls="store-tabpanel-2"
            disabled={!isAdmin}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ px: 3 }}>
        <TabPanel value={activeTab} index={0}>
          <LiveOpsTab store={store} />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <InventoryTab storeId={store.id} storeName={store.name} />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          {isAdmin ? (
            <ConfigTab store={store} />
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LockIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
              <Typography variant="h6" sx={{ color: colors.textSecondary }}>
                Admin Access Required
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                You need admin privileges to view store configuration.
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Box>
    </Paper>
  );
}

export default StoreDetail;
